import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent, ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import { V1A_NAMESPACE } from '../constants';
import { loadGeoData } from '../geo/geo';
import { useHtmlLang } from '../hooks/use-html-lang';
import { useV1aTrial } from '../trial-context';
import type { MiConnectionId } from './connections';
import { MI_CONNECTIONS, MI_TOTAL } from './connections';
import type { MiEngine, MiTranslate } from './engine';
import { startMultiInternet } from './engine';
import styles from './multi-internet.module.css';

const B = <b />;

const CONNECTION_ICONS: Record<MiConnectionId, ReactElement> = {
  fibra: (
    <svg viewBox='0 0 24 24'>
      <path d='M3 18c4.75 0 5-11.5 9.75-11.5H14' />
      <circle cx='16' cy='6.5' r='2' />
      <path d='M19 3.5l1.25-1.25M19.75 6.5H21.5M19 9.5l1.25 1.25' />
    </svg>
  ),
  wifi: (
    <svg viewBox='0 0 24 24'>
      <path d='M3 9.75a13 13 0 0 1 18 0M6.25 13a8.5 8.5 0 0 1 11.5 0M9.5 16.25a4.25 4.25 0 0 1 5 0' />
      <circle cx='12' cy='19.75' r='.9' />
    </svg>
  ),
  g5: (
    <svg viewBox='0 0 24 24'>
      <rect x='6.5' y='2.75' width='11' height='18.5' rx='2.25' />
      <path d='M10.5 18.25h3M8.75 12.25a4.75 4.75 0 0 1 6.5 0' />
      <circle cx='12' cy='14.75' r='.9' />
    </svg>
  ),
  cabo: (
    <svg viewBox='0 0 24 24'>
      <path d='M5 3h14v9.5h-3.5v3h-7v-3H5z' />
      <path d='M8.5 3v3M12 3v3M15.5 3v3' />
      <path d='M12 15.5v5.5' />
    </svg>
  ),
  isp2: (
    <svg viewBox='0 0 24 24'>
      <rect x='3' y='13' width='18' height='7.5' rx='2' />
      <path d='M6.5 16.75h.01M9.75 16.75h.01M16.5 13V6.25M13.25 6a4.5 4.5 0 0 1 6.5 0' />
    </svg>
  ),
  g4: (
    <svg viewBox='0 0 24 24'>
      <rect x='3' y='14.5' width='3' height='6.5' rx='.9' />
      <rect x='8.2' y='10.75' width='3' height='10.25' rx='.9' />
      <rect x='13.4' y='7' width='3' height='14' rx='.9' />
      <rect x='18.6' y='3' width='3' height='18' rx='.9' />
    </svg>
  ),
};

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ?? character);
}

/**
 * The native Multi Internet card (the design's `<section class="mi">`, formerly an iframe). React renders the
 * markup in its baseline state (Fibra + Wi-Fi 6 summed, zero drops); `startMultiInternet` hydrates it in place.
 */
export function MultiInternet() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const lang = useHtmlLang();
  const { openTrial } = useV1aTrial();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    let engine: MiEngine | null = null;
    let cancelled = false;
    loadGeoData(lang).then((data) => {
      if (cancelled) return;
      try {
        engine = startMultiInternet(section, { t: t as unknown as MiTranslate, lang, data });
      } catch (error) {
        // the static card stays complete and truthful without the engine
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });
    return () => {
      cancelled = true;
      engine?.destroy();
    };
  }, [t, lang]);

  const handleCta = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openTrial();
  };

  const initialList = MI_CONNECTIONS.map((connection) => t(`mi.conns.${connection.id}.sr`));
  const nb = (text: string) => `<span class="nb">${escapeHtml(text)}</span>`;
  const statusInitial = t('mi.status_initial')
    .replace(/<stab>/g, '<b class="mi-stab">')
    .replace(/<\/stab>/g, '</b>');
  const sumSubInitial = `<span class="x-long">${t('mi.sum_sub.pair_long', { a: nb(t('mi.conns.fibra.name')), b: nb(t('mi.conns.wifi.name')), interpolation: { escapeValue: false } })}</span><span class="x-short">${t('mi.sum_sub.pair_short', { a: nb(t('mi.conns.fibra.name')), b: nb(t('mi.conns.wifi.name')), interpolation: { escapeValue: false } })}</span>`;
  const drops0 = t('mi.drops', { count: 0 });

  return (
    <div className={classNames(styles.root, 'v1-frame v1-frame--second relative mx-auto mt-[clamp(1.5rem,.5rem_+_2.5vw,2.5rem)] w-full max-w-[90rem] [container:v1f/inline-size]')} data-reveal=''>
      <section ref={sectionRef} className='mi' aria-labelledby='mi-title' data-mi=''>
        <div className='mi-card'>
          <header className='mi-head'>
            <span className='eyebrow'>{t('mi.eyebrow')}</span>
            <h2 className='mi-title' id='mi-title'>
              <span className='mi-title-a'>
                {t('mi.title_a')} <span className='mi-title-b'>{t('mi.title_b')}</span>
              </span>{' '}
              <em>{t('mi.title_em')}</em>
            </h2>
            <p className='mi-lead'>
              <span className='x-desk'>
                <Trans t={t} i18nKey='mi.lead_desk' components={{ b: B }} />
              </span>
              <span className='x-mobile'>
                <Trans t={t} i18nKey='mi.lead_mobile' components={{ b: B }} />
              </span>
            </p>
            <ul className='mi-facts'>
              <li>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M12 3.5 5 6.25v5.5c0 4.4 3 7.6 7 8.75 4-1.15 7-4.35 7-8.75v-5.5z' />
                  <path d='M12.75 8.5 10 12.5h4l-2.75 4' />
                </svg>
                <span>
                  <Trans t={t} i18nKey='mi.fact_failover' components={{ b: B }} />
                </span>
              </li>
              <li>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <circle cx='9.25' cy='8.25' r='3.25' />
                  <path d='M2.75 19.5c0-3.15 2.9-5.25 6.5-5.25s6.5 2.1 6.5 5.25' />
                  <path d='M16.25 5.4a3.05 3.05 0 0 1 0 5.7M17.9 14.6c2.15.62 3.35 2.4 3.35 4.9' />
                </svg>
                <span>
                  <Trans t={t} i18nKey='mi.fact_players' components={{ b: B }} />
                </span>
              </li>
            </ul>
          </header>
          <div className='mi-proof' role='group' aria-label={t('mi.proof_aria')} data-proof=''>
            <p className='sr-only' data-proof-sr=''>
              {t('mi.proof_sr_gain', { base: t('mi.cap_mb', { value: 500 }), sum: t('mi.cap_mb', { value: 800 }), pct: 60, drops: drops0 })}
            </p>
            <div className='mi-proof-rows' aria-hidden='true'>
              <div className='mi-proof-item mi-proof-item--bad'>
                <span className='mi-proof-line'>
                  <small>{t('mi.proof_bad')}</small>
                  <span className='mi-proof-sp' />
                  <strong>
                    <span data-p-base=''>500</span>
                    <span className='u' data-p-base-u=''>
                      {t('mi.unit_mb')}
                    </span>
                  </strong>
                </span>
                <span className='mi-proof-bar'>
                  <span className='mi-proof-fill' data-p-fill='' />
                </span>
              </div>
              <div className='mi-proof-item mi-proof-item--good'>
                <span className='mi-proof-line'>
                  <small>{t('mi.proof_good')}</small>
                  <span className='mi-proof-sp' />
                  <span className='mi-proof-pct' data-p-pct=''>
                    {t('mi.pct', { value: 60 })}
                  </span>
                  <strong>
                    <span data-p-sum=''>800</span>
                    <span className='u' data-p-sum-u=''>
                      {t('mi.unit_mb')}
                    </span>
                  </strong>
                </span>
                <span className='mi-proof-bar'>
                  <span className='mi-proof-fill' data-p-fill-good='' />
                </span>
              </div>
            </div>
            <p className='mi-proof-note' aria-hidden='true'>
              <span className='mi-proof-tag' data-p-drops=''>
                {drops0}
              </span>
              <Trans t={t} i18nKey='mi.proof_note' components={{ b: B }} />
            </p>
          </div>
          <p className='sr-only' id='mi-desc' data-mi-desc=''>
            {t('mi.desc', { list: initialList.join(', ') })}
          </p>
          <div className='mi-stage' data-stage='' role='group' aria-label={t('mi.stage_aria')} aria-describedby='mi-desc' dir='ltr'>
            <p className='sr-only' data-sr-now=''>
              {t('mi.sr_now.many', { combo: t('mi.combo', { count: 2 }), total: MI_TOTAL, cap: t('mi.cap_mb', { value: 800 }), drops: drops0, tail: t('mi.sr_now.tail_ok') })}
            </p>
            <svg className='mi-wires' data-wires='' aria-hidden='true' focusable='false' />
            <div className='mi-packets' data-packets='' aria-hidden='true' />
            <div className='mi-nojs-rail' aria-hidden='true' />
            <div className='mi-node mi-node--pc' data-node='pc'>
              <div className='mi-node-icon' aria-hidden='true'>
                <svg viewBox='0 0 40 40'>
                  <rect x='5' y='7' width='30' height='20' rx='3' />
                  <path d='M14 34h12M20 27v7' />
                  <circle cx='20' cy='17' r='5' />
                  <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
                  <circle className='f' cx='20' cy='17' r='1.4' />
                </svg>
              </div>
              <div className='mi-node-label'>
                <strong>{t('mi.pc')}</strong>
                <small>{t('mi.pc_sub')}</small>
              </div>
            </div>
            <div className='mi-routes-wrap'>
              <div className='mi-key'>
                <span className='mi-key-col' aria-hidden='true' data-key=''>
                  <b data-key-label=''>{t('mi.key', { count: 2, total: MI_TOTAL })}</b>
                </span>
                <p className='mi-hint' data-hint='' aria-hidden='true'>
                  <svg className='x-touch' viewBox='0 0 24 24'>
                    <circle className='dot' cx='15' cy='15' r='2.4' />
                    <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                  </svg>
                  <svg className='x-hover' viewBox='0 0 24 24'>
                    <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                  </svg>
                  <svg className='x-after' viewBox='0 0 24 24'>
                    <path d='M4.5 12.5l4.5 4.5L19.5 6.5' />
                  </svg>
                  <span className='x-touch'>{t('mi.hint_touch')}</span>
                  <span className='x-hover'>{t('mi.hint_hover')}</span>
                  <span className='x-after'>{t('mi.hint_after')}</span>
                </p>
              </div>
              <svg className='mi-defs' aria-hidden='true' focusable='false'>
                <defs>
                  <linearGradient id='mi-spg' x1='0' y1='0' x2='0' y2='1'>
                    <stop className='g0' offset='0' />
                    <stop className='g1' offset='1' />
                  </linearGradient>
                </defs>
              </svg>
              <ul className='mi-routes' data-routes='' aria-label={t('mi.routes_aria')}>
                {MI_CONNECTIONS.map((connection) => {
                  const name = t(`mi.conns.${connection.id}.sr`);
                  return (
                    <li key={connection.id}>
                      <button className={classNames('mi-route', connection.on ? 'is-on' : 'is-off')} type='button' data-conn={connection.id} data-mbps={connection.mbps} aria-pressed={connection.on ? 'true' : 'false'} aria-label={t(connection.on ? 'mi.conn_aria_on' : 'mi.conn_aria_off', { name, mbps: connection.mbps })}>
                        <span className='mi-route-cc' aria-hidden='true'>
                          {CONNECTION_ICONS[connection.id]}
                        </span>
                        <span className='mi-route-body'>
                          <span className='mi-route-city'>{t(`mi.conns.${connection.id}.name`)}</span>
                          <span className='mi-route-meta'>
                            <span className='mi-route-kind'>{t(`mi.conns.${connection.id}.kind`)}</span>
                            <span className='mi-route-tag' data-tag=''>
                              {t(connection.on ? 'mi.tag_on' : 'mi.tag_off')}
                            </span>
                          </span>
                        </span>
                        <svg className='mi-route-spark' viewBox='0 0 52 18' aria-hidden='true' focusable='false'>
                          <polygon className='sp-fill' points='' />
                          <polyline points='' />
                          <circle className='sp-dot' r='1.75' />
                        </svg>
                        <span className='mi-route-lat'>
                          <span className='mi-route-ms'>
                            <span data-mb=''>{connection.mbps}</span>
                            <span className='u'>{t('mi.unit_mb')}</span>
                          </span>
                          <span className='mi-route-delta' data-delta=''>
                            {t(connection.on ? 'mi.delta_on' : 'mi.delta_off')}
                          </span>
                        </span>
                        <span className='mi-route-tg' aria-hidden='true'>
                          <svg viewBox='0 0 24 24'>
                            <path className='t-off' d='M12 5.5v13M5.5 12h13' />
                            <path className='t-on' d='M5 12.5l4.5 4.5L19 7' />
                            <path className='t-down' d='M19 12a7 7 0 1 1-2.05-4.95M19 4.5v3.5h-3.5' />
                          </svg>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className='mi-node mi-node--np' data-node='np'>
              <div className='mi-node-icon' aria-hidden='true'>
                <svg viewBox='0 0 40 40'>
                  <path d='M5 11h6c6 0 7 9 13 9' />
                  <path d='M5 29h6c6 0 7-9 13-9' />
                  <path d='M5 20h19' />
                  <path d='M24 20h10.5M30.5 16.5 34.5 20l-4 3.5' />
                  <circle className='f' cx='24' cy='20' r='2.3' />
                </svg>
                <span className='mi-burst' />
              </div>
              <div className='mi-node-label'>
                <strong>{t('mi.np')}</strong>
                <small className='mi-node-sub'>
                  <span data-np-sub=''>{t('mi.np_sub.all')}</span>
                  <span className='mi-node-ghost' aria-hidden='true'>
                    {t('mi.np_sub.took_over')}
                  </span>
                  <span className='mi-node-ghost' aria-hidden='true'>
                    {t('mi.np_sub.all')}
                  </span>
                </small>
              </div>
            </div>
            <div className='mi-node mi-node--game' data-node='game'>
              <div className='mi-node-icon' aria-hidden='true'>
                <svg viewBox='0 0 40 40'>
                  <rect x='7' y='7' width='26' height='10' rx='2.5' />
                  <rect x='7' y='23' width='26' height='10' rx='2.5' />
                  <path d='M12 12h6M12 28h6' />
                  <circle className='f' cx='27' cy='12' r='1.6' />
                  <circle className='f' cx='27' cy='28' r='1.6' />
                </svg>
              </div>
              <div className='mi-node-label'>
                <strong>{t('mi.game')}</strong>
                <small className='mi-node-sub'>
                  <span data-game-sub=''>{t('mi.game_sub.ok')}</span>
                  <span className='mi-node-ghost' aria-hidden='true'>
                    {t('mi.game_sub.ok')}
                  </span>
                </small>
              </div>
            </div>
            <div className='mi-backup' data-backup='' aria-hidden='true'>
              <p className='mi-direct-key'>{t('mi.backup_key')}</p>
              <div className='mi-backup-row'>
                <svg className='mi-backup-fan' viewBox='0 0 44 22' aria-hidden='true' focusable='false'>
                  <path className='bf-l' d='M22 3 2 19M22 3 6.4 19M22 3 10.9 19M22 3 15.3 19M22 3 19.8 19M22 3 24.2 19M22 3 28.7 19M22 3 33.1 19M22 3 37.6 19M22 3 42 19' />
                  <path className='bf-d' d='M2 19v0M6.4 19v0M10.9 19v0M15.3 19v0M19.8 19v0M24.2 19v0M28.7 19v0M33.1 19v0M37.6 19v0M42 19v0' />
                </svg>
                <b data-backup-b=''>{t('mi.backup.rest_b')}</b>
                <strong>10</strong>
                <small className='mi-backup-sub'>
                  <span data-backup-sub=''>{t('mi.backup.rest_sub')}</span>
                  {(['rest', 'hot', 'lone', 'none'] as const).map((state) => (
                    <span key={state} className='mi-node-ghost' aria-hidden='true'>
                      {t(`mi.backup.${state}_sub`)}
                    </span>
                  ))}
                </small>
              </div>
            </div>
            <div className='mi-result' aria-hidden='true'>
              <p className='mi-direct-key'>{t('mi.result_key')}</p>
              <div className='mi-direct' data-sum-row=''>
                <i />
                <span className='mi-direct-txt'>
                  <b data-sum-b=''>{t('mi.sum_b.ok')}</b>
                  <span className='x-sep' />
                  {/* eslint-disable-next-line react/no-danger -- engine-owned markup, built from our own locale templates with every name escaped */}
                  <span className='mi-direct-sub' data-sum-sub='' dangerouslySetInnerHTML={{ __html: sumSubInitial }} />
                </span>
                <strong>
                  <span data-sum=''>800</span>
                  <span className='u' data-sum-u=''>
                    {t('mi.unit_mb')}
                  </span>
                </strong>
              </div>
            </div>
          </div>
          <footer className='mi-foot'>
            <div className='mi-foot-text'>
              <p className='mi-status' data-status=''>
                <i aria-hidden='true' />
                {/* eslint-disable-next-line react/no-danger -- engine-owned markup, built from our own locale templates */}
                <span className='mi-status-body' data-status-text='' dangerouslySetInnerHTML={{ __html: statusInitial }} />
              </p>
              <div className='mi-foot-slot'>
                <p className='mi-bridge' data-bridge=''>
                  {t('mi.bridge')}
                </p>
              </div>
            </div>
            <div className='mi-actions'>
              <button className='mi-pause' type='button' data-pause='' aria-label={t('mi.pause')} aria-pressed='false' title={t('mi.pause_title')}>
                <svg className='ic-pause' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M8.5 5.5v13M15.5 5.5v13' />
                </svg>
                <svg className='ic-play' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
                </svg>
              </button>
              <button className='btn btn-secondary mi-btn' type='button' data-drop='' aria-busy='false' title={t('mi.drop_title')}>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M13.5 3 6 13h5l-1 8L18 11h-5z' />
                  <path d='M4 4l16 16' />
                </svg>
                <span className='mi-btn-lbl'>
                  <span data-lbl=''>{t('mi.drop')}</span>
                  <span className='mi-btn-ghost' aria-hidden='true'>
                    {t('mi.drop')}
                  </span>
                  <span className='mi-btn-ghost' aria-hidden='true'>
                    {t('mi.drop_busy')}
                  </span>
                  <span className='mi-btn-ghost' aria-hidden='true'>
                    {t('mi.drop_back')}
                  </span>
                </span>
              </button>
              <div className='mi-offer'>
                <a className='btn btn-primary mi-cta' data-cta='' href='#cadastro' onClick={handleCta}>
                  <span>{t('mi.cta')}</span>
                  <svg viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M5 12h13M13 6l6 6-6 6' />
                  </svg>
                </a>
                <p className='mi-offer-note'>
                  <Trans t={t} i18nKey='mi.offer_note' components={{ b: B }} />
                </p>
              </div>
            </div>
            <p className='sr-only' role='status' aria-live='polite' data-live='' />
          </footer>
        </div>
      </section>
    </div>
  );
}
